import React, { useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings as SettingsIcon, Menu as MenuIcon, Building2, Globe, Mail, Phone, Languages, Clock, DollarSign, Percent, Check, ChevronDown, Lock, Sparkles, MapPin, Image as ImageIcon, ImageOff, Upload, Trash2, Layers, Instagram, Facebook } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useSettings, GeneralSettings, currencyMeta, languageMeta, timezoneOptions, Currency, Language, MenuItemImagePolicy } from './SettingsContext';
import { useBranches } from './BranchesContext';
import { Toggle } from './Toggle';
type Tab = 'general' | 'brand' | 'users' | 'reservation';
const tabs: {
  key: Tab;
  label: string;
  locked: boolean;
}[] = [{
  key: 'general',
  label: 'General',
  locked: false
}, {
  key: 'brand',
  label: 'Brand',
  locked: true
}, {
  key: 'users',
  label: 'User Management',
  locked: true
}, {
  key: 'reservation',
  label: 'Reservation',
  locked: true
}];
export function SettingsPage() {
  const {
    general,
    updateGeneral
  } = useSettings();
  const {
    branches
  } = useBranches();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [savedFlash, setSavedFlash] = useState(false);
  const flashTimer = useRef<number | null>(null);
  const flashSaved = () => {
    setSavedFlash(true);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setSavedFlash(false), 1200);
  };
  const update = (patch: Partial<GeneralSettings>) => {
    updateGeneral(patch);
    flashSaved();
  };
  return <div className="flex h-screen w-full bg-[#f9fafb] overflow-hidden font-sans flex-col md:flex-row" data-id="element-3224">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between bg-[#0f172a] text-white p-4 flex-shrink-0 z-30" data-id="element-3225">
        <button onClick={() => setIsSidebarOpen(true)} className="p-1 hover:bg-white/10 rounded-md transition-colors" data-id="element-3226">
          <MenuIcon size={24} data-id="element-3227" />
        </button>
        <span className="font-semibold" data-id="element-3228">Settings</span>
        <div className="w-6" data-id="element-3229" />
      </div>

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(v => !v)} data-id="element-3230" />

      <main className="flex-1 flex flex-col h-full overflow-hidden" data-id="element-3231">
        {/* Header */}
        <div className="px-4 md:px-6 pt-5 md:pt-6 pb-0 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-3232">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap" data-id="element-3233">
            <div data-id="element-3234">
              <div className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1 flex items-center gap-1.5" data-id="element-3235">
                <SettingsIcon size={13} data-id="element-3236" />
                Business
              </div>
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3237">Settings</h1>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-3238">
                Manage your business identity, contact, and defaults
              </p>
            </div>
            <AnimatePresence data-id="element-3239">
              {savedFlash && <motion.span initial={{
              opacity: 0,
              y: -4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-bold bg-emerald-100 text-emerald-700" data-id="element-3240">
                  <Check size={12} strokeWidth={3} data-id="element-3241" />
                  Saved
                </motion.span>}
            </AnimatePresence>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 -mb-px overflow-x-auto" data-id="element-3242">
            {tabs.map(t => {
            const isActive = activeTab === t.key;
            return <button key={t.key} onClick={() => !t.locked && setActiveTab(t.key)} disabled={t.locked} className={`relative px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 ${isActive ? 'text-orange-600' : t.locked ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3243">
                  {t.label}
                  {t.locked && <Lock size={11} className="text-gray-300" data-id="element-3244" />}
                  {isActive && <motion.div layoutId="settings-tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30
              }} data-id="element-3245" />}
                </button>;
          })}
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto" data-id="element-3246">
          {activeTab === 'general' && <div className="max-w-3xl mx-auto p-5 md:p-8 space-y-7" data-id="element-3247">
              {/* Business identity */}
              <Section icon={<Building2 size={16} data-id="element-3249" />} title="Business identity" description="How your business appears to customers and staff" data-id="element-3248">
                <div className="flex flex-col sm:flex-row gap-5" data-id="element-3250">
                  {/* Identity fields */}
                  <div className="flex-1 space-y-4 min-w-0" data-id="element-3251">
                    <Field label="Business name" data-id="element-3252">
                      <input value={general.businessName} onChange={e => update({
                    businessName: e.target.value
                  })} placeholder="Your business name" className={inputClass} data-id="element-3253" />
                    </Field>
                    <Field label="Tagline" hint="A short phrase that captures what you do" data-id="element-3254">
                      <input value={general.tagline} onChange={e => update({
                    tagline: e.target.value
                  })} placeholder="One-liner tagline" className={inputClass} data-id="element-3255" />
                    </Field>
                  </div>
                </div>

                <Field label="About your business" hint="Used on your customer-facing pages" data-id="element-3256">
                  <textarea value={general.description} onChange={e => update({
                description: e.target.value
              })} rows={3} placeholder="Tell your customers a bit about your business..." className={`${inputClass} resize-none leading-relaxed`} data-id="element-3257" />
                  <div className="text-[11px] text-gray-400 mt-1 text-right tabular-nums" data-id="element-3258">
                    {general.description.length} / 280
                  </div>
                </Field>
              </Section>

              {/* Contact */}
              <Section icon={<Phone size={16} data-id="element-3260" />} title="Contact information" description="How customers and partners can reach you" data-id="element-3259">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3261">
                  <Field label="Email" icon={<Mail size={16} data-id="element-3263" />} data-id="element-3262">
                    <input type="email" value={general.email} onChange={e => updateGeneral({
                  email: e.target.value
                })} placeholder="hello@example.com" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3264" />
                  </Field>
                  <Field label="Phone" icon={<Phone size={16} data-id="element-3266" />} data-id="element-3265">
                    <input type="tel" value={general.phone} onChange={e => updateGeneral({
                  phone: e.target.value
                })} placeholder="+251 911 000 000" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3267" />
                  </Field>
                </div>
              </Section>

              <FormSection title="Social & Web" description="Links to your online presence" data-id="element-3268">
                <div className="space-y-4" data-id="element-3269">
                  <Field label="Website" icon={<Globe size={16} data-id="element-3271" />} data-id="element-3270">
                    <input type="url" value={general.website} onChange={e => updateGeneral({
                  website: e.target.value
                })} placeholder="https://yourbusiness.com" className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3272" />
                  </Field>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3273">
                    <Field label="Instagram" icon={<Instagram size={16} data-id="element-3275" />} data-id="element-3274">
                      <input type="text" value={general.instagram} onChange={e => updateGeneral({
                    instagram: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3276" />
                    </Field>
                    <Field label="Facebook" icon={<Facebook size={16} data-id="element-3278" />} data-id="element-3277">
                      <input type="text" value={general.facebook} onChange={e => updateGeneral({
                    facebook: e.target.value
                  })} className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100" data-id="element-3279" />
                    </Field>
                  </div>
                </div>
              </FormSection>

              {/* Localization */}
              <Section icon={<Globe size={16} data-id="element-3281" />} title="Localization" description="Currency, time, and language preferences" data-id="element-3280">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3282">
                  <Field label="Currency" icon={<DollarSign size={12} data-id="element-3284" />} data-id="element-3283">
                    <SelectField value={general.currency} onChange={v => update({
                  currency: v as Currency
                })} options={Object.entries(currencyMeta).map(([k, v]) => ({
                  value: k,
                  label: v.label
                }))} data-id="element-3285" />
                  </Field>
                  <Field label="Language" icon={<Languages size={12} data-id="element-3287" />} data-id="element-3286">
                    <SelectField value={general.language} onChange={v => update({
                  language: v as Language
                })} options={Object.entries(languageMeta).map(([k, v]) => ({
                  value: k,
                  label: `${v.label} (${v.native})`
                }))} data-id="element-3288" />
                  </Field>
                </div>
                <Field label="Timezone" icon={<Clock size={12} data-id="element-3290" />} data-id="element-3289">
                  <SelectField value={general.timezone} onChange={v => update({
                timezone: v
              })} options={timezoneOptions} data-id="element-3291" />
                </Field>
              </Section>

              {/* Defaults */}
              <Section icon={<MapPin size={16} data-id="element-3293" />} title="System defaults" description="Defaults used when no branch context is set" data-id="element-3292">
                <Field label="Default branch" hint="Used for reports and notifications" data-id="element-3294">
                  <SelectField value={general.defaultBranchId || ''} onChange={v => update({
                defaultBranchId: v || null
              })} options={[{
                value: '',
                label: 'No default'
              }, ...branches.map(b => ({
                value: b.id,
                label: b.name
              }))]} data-id="element-3295" />
                </Field>
              </Section>

              {/* Menu item images */}
              <Section icon={<ImageIcon size={16} data-id="element-3297" />} title="Menu item images" description="How menu items display images across customer-facing menus" data-id="element-3296">
                <MenuItemImagePolicyPicker policy={general.menuItemImagePolicy} defaultImage={general.defaultMenuItemImage} onPolicyChange={p => update({
              menuItemImagePolicy: p
            })} onDefaultImageChange={url => update({
              defaultMenuItemImage: url
            })} data-id="element-3298" />
              </Section>

              {/* Commissions */}
              <Section icon={<Percent size={16} data-id="element-3300" />} title="Payment Commissions" description="Platform commission rates applied to orders by payment method" data-id="element-3299">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3301">
                  <Field label="Telebirr (%)" data-id="element-3302">
                    <input type="number" step="0.01" min="0" value={general.commissionRates.telebirr} onChange={e => update({
                  commissionRates: {
                    ...general.commissionRates,
                    telebirr: parseFloat(e.target.value) || 0
                  }
                })} className={inputClass} data-id="element-3303" />
                  </Field>
                  <Field label="Bank Transfer (%)" data-id="element-3304">
                    <input type="number" step="0.01" min="0" value={general.commissionRates['bank-transfer']} onChange={e => update({
                  commissionRates: {
                    ...general.commissionRates,
                    'bank-transfer': parseFloat(e.target.value) || 0
                  }
                })} className={inputClass} data-id="element-3305" />
                  </Field>
                  <Field label="CBE Birr (%)" data-id="element-3306">
                    <input type="number" step="0.01" min="0" value={general.commissionRates['cbe-birr']} onChange={e => update({
                  commissionRates: {
                    ...general.commissionRates,
                    'cbe-birr': parseFloat(e.target.value) || 0
                  }
                })} className={inputClass} data-id="element-3307" />
                  </Field>
                  <Field label="Card (%)" data-id="element-3308">
                    <input type="number" step="0.01" min="0" value={general.commissionRates.card} onChange={e => update({
                  commissionRates: {
                    ...general.commissionRates,
                    card: parseFloat(e.target.value) || 0
                  }
                })} className={inputClass} data-id="element-3309" />
                  </Field>
                </div>
              </Section>

              <div className="text-center text-xs text-gray-400 py-4" data-id="element-3310">
                Changes save automatically as you edit.
              </div>
            </div>}

          {activeTab !== 'general' && <LockedTabPlaceholder label={tabs.find(t => t.key === activeTab)?.label || ''} data-id="element-3311" />}
        </div>
      </main>
    </div>;
}
const inputClass = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition';
function Section({
  icon,
  title,
  description,
  children
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 shadow-sm" data-id="element-3312">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-3313">
        <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center" data-id="element-3314">
          {icon}
        </div>
        <div data-id="element-3315">
          <h3 className="font-bold text-gray-900 text-base" data-id="element-3316">{title}</h3>
          {description && <p className="text-xs text-gray-500" data-id="element-3317">{description}</p>}
        </div>
      </div>
      <div className="space-y-4" data-id="element-3318">{children}</div>
    </section>;
}
function Field({
  label,
  hint,
  icon,
  children
}: {
  label: string;
  hint?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-3319">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-3320">
        {icon}
        {label}
      </div>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1" data-id="element-3321">{hint}</p>}
    </label>;
}
function SelectField({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: {
    value: string;
    label: string;
  }[];
}) {
  return <div className="relative" data-id="element-3322">
      <select value={value} onChange={e => onChange(e.target.value)} className={`${inputClass} appearance-none pr-9`} data-id="element-3323">
        {options.map(o => <option key={o.value} value={o.value} data-id="element-3324">
            {o.label}
          </option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-3325" />
    </div>;
}
function PercentInput({
  value,
  onChange
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  return <div className="relative" data-id="element-3326">
      <input type="number" min={0} max={100} step={0.5} value={value} onChange={e => {
      const n = parseFloat(e.target.value);
      onChange(isNaN(n) ? 0 : Math.max(0, Math.min(100, n)));
    }} className={`${inputClass} pr-9 tabular-nums`} data-id="element-3327" />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold pointer-events-none" data-id="element-3328">
        %
      </span>
    </div>;
}
function FormSection({
  title,
  description,
  children
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return <section className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 shadow-sm" data-id="element-3329">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-3330">
        <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center" data-id="element-3331">
          <Building2 size={16} data-id="element-3332" />
        </div>
        <div data-id="element-3333">
          <h3 className="font-bold text-gray-900 text-base" data-id="element-3334">{title}</h3>
          {description && <p className="text-xs text-gray-500" data-id="element-3335">{description}</p>}
        </div>
      </div>
      <div className="space-y-4" data-id="element-3336">{children}</div>
    </section>;
}
function LockedTabPlaceholder({
  label
}: {
  label: string;
}) {
  return <div className="h-full flex items-center justify-center p-10" data-id="element-3337">
      <div className="text-center max-w-sm" data-id="element-3338">
        <div className="w-14 h-14 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3" data-id="element-3339">
          <Lock size={22} className="text-gray-400" data-id="element-3340" />
        </div>
        <h3 className="font-semibold text-gray-900 text-base mb-1" data-id="element-3341">
          {label} settings
        </h3>
        <p className="text-sm text-gray-500" data-id="element-3342">
          This section is coming soon. For now, use the General tab to configure
          your business.
        </p>
      </div>
    </div>;
}
function MenuItemImagePolicyPicker({
  policy,
  defaultImage,
  onPolicyChange,
  onDefaultImageChange
}: {
  policy: MenuItemImagePolicy;
  defaultImage: string | null;
  onPolicyChange: (p: MenuItemImagePolicy) => void;
  onDefaultImageChange: (url: string | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = e => onDefaultImageChange(e.target?.result as string);
    reader.readAsDataURL(file);
  };
  const options: {
    key: MenuItemImagePolicy;
    title: string;
    description: string;
    Icon: ComponentType<{
      size?: number;
    }>;
    color: 'orange' | 'blue' | 'gray';
  }[] = [{
    key: 'per-item',
    title: 'Per-item images',
    description: 'Each item shows its uploaded image; items without one show a placeholder.',
    Icon: Layers,
    color: 'orange'
  }, {
    key: 'default',
    title: 'Use a default image',
    description: 'Items without an image fall back to a single shared default image.',
    Icon: ImageIcon,
    color: 'blue'
  }, {
    key: 'none',
    title: 'No images at all',
    description: 'Show a clean, text-only menu — useful for fast browsing or low-bandwidth.',
    Icon: ImageOff,
    color: 'gray'
  }];
  const colorClasses: Record<string, {
    border: string;
    bg: string;
    ring: string;
    iconBg: string;
    text: string;
  }> = {
    orange: {
      border: 'border-orange-300',
      bg: 'bg-orange-50',
      ring: 'ring-orange-100',
      iconBg: 'bg-orange-100 text-orange-600',
      text: 'text-orange-700'
    },
    blue: {
      border: 'border-blue-300',
      bg: 'bg-blue-50',
      ring: 'ring-blue-100',
      iconBg: 'bg-blue-100 text-blue-600',
      text: 'text-blue-700'
    },
    gray: {
      border: 'border-gray-400',
      bg: 'bg-gray-50',
      ring: 'ring-gray-100',
      iconBg: 'bg-gray-200 text-gray-600',
      text: 'text-gray-700'
    }
  };
  return <div className="space-y-3" data-id="element-3343">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-3344">
        {options.map(({
        key,
        title,
        description,
        Icon,
        color
      }) => {
        const isActive = policy === key;
        const c = colorClasses[color];
        return <button key={key} type="button" onClick={() => onPolicyChange(key)} className={`relative text-left rounded-xl border-2 p-3 transition-all ${isActive ? `${c.border} ${c.bg} ring-2 ${c.ring}` : 'border-gray-200 bg-white hover:border-gray-300'}`} data-id="element-3345">
              <div className="flex items-start gap-2" data-id="element-3346">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? c.iconBg : 'bg-gray-100 text-gray-400'}`} data-id="element-3347">
                  <Icon size={14} data-id="element-3348" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-3349">
                  <div className={`text-sm font-bold leading-tight ${isActive ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-3350">
                    {title}
                  </div>
                  <div className="text-[11px] text-gray-500 leading-snug mt-1" data-id="element-3351">
                    {description}
                  </div>
                </div>
              </div>
              {isActive && <Check size={14} strokeWidth={3} className={`absolute top-2 right-2 ${c.text}`} data-id="element-3352" />}
            </button>;
      })}
      </div>

      <AnimatePresence initial={false} data-id="element-3353">
        {policy === 'default' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-3354">
            <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-3" data-id="element-3355">
              <div className="flex items-center gap-2 mb-2" data-id="element-3356">
                <ImageIcon size={14} className="text-blue-600" data-id="element-3357" />
                <span className="text-xs font-bold text-blue-900 uppercase tracking-wide" data-id="element-3358">
                  Default image
                </span>
              </div>
              <div className="flex items-start gap-3" data-id="element-3359">
                <div className="w-20 h-20 rounded-lg overflow-hidden border border-blue-200 bg-white flex-shrink-0" data-id="element-3360">
                  {defaultImage ? <img src={defaultImage} alt="Default menu item" className="w-full h-full object-cover" data-id="element-3361" /> : <div className="w-full h-full flex items-center justify-center text-blue-300" data-id="element-3362">
                      <ImageIcon size={28} data-id="element-3363" />
                    </div>}
                </div>
                <div className="flex-1 min-w-0 space-y-1.5" data-id="element-3364">
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={e => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }} className="hidden" data-id="element-3365" />
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-blue-300 text-blue-700 text-xs font-semibold hover:bg-blue-100 transition-colors" data-id="element-3366">
                    <Upload size={12} data-id="element-3367" />
                    {defaultImage ? 'Replace image' : 'Upload image'}
                  </button>
                  {defaultImage && <button type="button" onClick={() => onDefaultImageChange(null)} className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 text-gray-500 text-xs font-semibold hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors" data-id="element-3368">
                      <Trash2 size={12} data-id="element-3369" />
                      Remove
                    </button>}
                  <p className="text-[10px] text-blue-700/70 leading-snug" data-id="element-3370">
                    Used wherever an item has no image of its own. Item-specific
                    images still take priority.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {policy === 'none' && <div className="rounded-md border border-gray-200 bg-gray-50 p-2.5 flex items-start gap-2" data-id="element-3371">
          <ImageOff size={13} className="text-gray-500 flex-shrink-0 mt-0.5" data-id="element-3372" />
          <p className="text-[11px] text-gray-700 leading-snug" data-id="element-3373">
            All menu item images will be hidden across customer-facing menus.
            Image uploads on individual items will be ignored.
          </p>
        </div>}
    </div>;
}